import styles from "./styles.module.scss";
import { RegisterOptions, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import React from "react";
import ToolTip from "app/routes/Scheduling/ToolTip";

const FormInput = ({
  tagTitle,
  fieldName,
  image = null,
  defaultValue = null,
  isRequired = false,
  shouldCheck = false,
  regex = null,
  isEditable = true,
  highField = false,
  checkMessage = null,
  error = null,
  hideLabel = false,
  longField = false,
  addDollar = false,
  placeholder = null,
  isNumberOnly = null,
  tooltipText = null,
  tooltipColor = null,
  // Which way the tooltip bubble opens. Defaults to ToolTip's own 'left'
  // (anchored at the icon, opening rightwards); pass "right" for fields close
  // to the right edge of a panel, where that bubble would be clipped.
  tooltipOrientation = null,
  // Vertical direction the bubble opens; pass "bottom" for fields near the top
  // of a scrolling column, where an upward bubble is clipped.
  tooltipPlacement = null,
  // Cap the bubble width for fields inside narrow columns.
  tooltipMaxWidth = null,
  currency = { currency: "" },
  maxLength = null,
  onlyInteger = false,
  multiline = false,
  min = null,
  max = null,
  // Custom rules, same shape as react-hook-form's `validate`. Pass these here
  // rather than spreading `register(...)` onto this component — the spread hands
  // it a `ref` it can't hold, so the registration never attaches and submits die.
  validate = null,
  // A fixed unit shown after the input, on the field's own row ("3 Billing
  // Cycles"). Sits here rather than in a second column beside the field so the
  // label keeps the full width — a long one wraps mid-phrase at half width.
  // The mirror image of addDollar's prefix.
  unit = null,
}) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const registerProps = (() => {
    const validations: RegisterOptions = {};

    if (isRequired) {
      validations.required = t("This field is required");
    }

    if (onlyInteger) {
      validations.pattern = {
        value: /^[0-9]+$/,
        message: t("This field requires whole numbers"),
      };
    } else if (shouldCheck && regex && checkMessage) {
      validations.pattern = {
        value: regex,
        message: checkMessage,
      };
    }

    if (min !== null) {
      validations.min = {
        value: min,
        message: t(`Value must be at least ${min}`),
      };
    }

    if (max !== null) {
      validations.max = {
        value: max,
        message: t(`Value must be at most ${max}`),
      };
    }

    if (validate) {
      validations.validate = validate;
    }

    return Object.keys(validations).length > 0
      ? register(fieldName, validations)
      : register(fieldName);
  })();

  const inputStyling = isEditable
    ? addDollar
      ? styles.input_with_dollar
      : styles.input_container
    : styles.input_container_not_editable;
  const required_star = <span className={styles.required}>*</span>;
  const required_dollar = addDollar ? <span>{currency?.currency}</span> : null;

  const handleKeyPress = (event) => {
    const keyCode = event.keyCode || event.which;
    const keyValue = String.fromCharCode(keyCode);
    const regex = onlyInteger ? /^[0-9\b]+$/ : /^[0-9.\b]+$/;

    if (!regex.test(keyValue)) {
      event.preventDefault();
    }
  };

  return (
    <div className={styles.grow1}>
      {!hideLabel && (
        <div className={styles.select_container}>
          <p
            className={`${styles.tag} ${
              highField ? styles.high_field_tag : ""
            }`}
          >
            {tagTitle} {isRequired ? required_star : null}
          </p>
          {errors && errors[fieldName] && (
            <span className={styles.errorText}>
              {errors[fieldName].message}
            </span>
          )}
          {error && (
            <span className={styles.errorText}>
              {error}
            </span>
          )}
          {tooltipText && (
            <ToolTip
              text={tooltipText}
              iconColor={tooltipColor}
              orientation={tooltipOrientation || undefined}
              placement={tooltipPlacement || undefined}
              maxWidth={tooltipMaxWidth || undefined}
            />
          )}
        </div>
      )}
      <div
        className={`${
          addDollar
            ? styles.dollar_line_container
            : styles.line_container
        } ${highField ? styles.p_13_8 : styles.p_10} ${isEditable ? '' : styles.disabled_field} `}
      >
        {required_dollar}
        {multiline ? (
          <textarea
            defaultValue={defaultValue}
            data-cy={fieldName}
            data-testid={fieldName}
            placeholder={placeholder}
            className={`${inputStyling} ${image ? styles.ml_10 : ""} ${styles.height_100}`}
            {...registerProps}
            readOnly={!isEditable}
            rows={8}
            maxLength={maxLength || undefined}
          />
        ) : (
          <input
            defaultValue={defaultValue}
            data-cy={fieldName}
            data-testid={fieldName}
            placeholder={placeholder}
            className={`${inputStyling} ${image ? styles.ml_10 : ""} ${
              longField ? styles.long_field : ""
            } ${addDollar ? styles.input_with_dollar : ""} ${
              unit ? styles.input_with_unit : ""
            }`}
            {...registerProps}
            readOnly={!isEditable}
            onKeyPress={
              addDollar || isNumberOnly || onlyInteger ? handleKeyPress : null
            }
            maxLength={maxLength || undefined}
            min={min !== null ? min : undefined}
            max={max !== null ? max : undefined}
            type={onlyInteger || isNumberOnly ? "number" : undefined}
          />
        )}
        {unit && <span className={styles.unit_suffix}>{unit}</span>}
      </div>
    </div>
  );
};

export default FormInput;
